import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { InfoOutlinedIcon } from '@/assets/icons/info-outlined';
import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { RadioItem } from '@/components/RadioItem';
import { SheetScreen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { AccountLink } from '@/feature/account-details/components/AccountLink';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import {
    DefaultVertical,
    mapDefaultVerticalToProduct,
    mapProductToDefaultVertical,
    useAuthUserSettings,
    useUpdateUserSettings,
} from '@/hooks/use-auth-user-settings';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, getProductType, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { navigateToProduct } from '@/navigation/navigation';
import { designSystem } from '@/styles/styles';

export const DefaultProductSelectScreen = () => {
    const { t } = useTranslation(['product_switch_menu', 'account', 'common']);
    const navigation = useNavigation();
    const products = useJurisdictionStore(state => state.jurisdictionSettings?.products ?? []);
    const setProduct = useJurisdictionStore(state => state.actions.setProduct);
    const { data: userSettings } = useAuthUserSettings();
    const { mutate: updateUserSettings } = useUpdateUserSettings();
    const { isProductAvailable } = useAvailableProducts();
    const { showToast } = useAlerts();

    const allVerticals = useMemo(() => {
        return products
            .map(p => getProductType(p.uid))
            .filter((product, i, arr) => arr.indexOf(product) === i)
            .map(mapProductToDefaultVertical)
            .filter((v): v is DefaultVertical => v !== null);
    }, [products]);

    const [pendingVertical, setPendingVertical] = useState<DefaultVertical | null>(null);
    const currentDefaultVertical = userSettings?.default_vertical ?? null;
    const selectedVertical = pendingVertical ?? currentDefaultVertical;

    const selectedProduct = selectedVertical ? mapDefaultVerticalToProduct(selectedVertical) : Product.None;
    const canUpdate =
        selectedProduct !== Product.None &&
        isProductAvailable(selectedProduct) &&
        selectedVertical !== currentDefaultVertical;

    const hasDisabled = allVerticals.some(v => !isProductAvailable(mapDefaultVerticalToProduct(v)));

    const handleUpdate = () => {
        if (!selectedVertical || !canUpdate) {
            return;
        }

        updateUserSettings(
            { default_vertical: selectedVertical },
            {
                onSuccess: () => {
                    showToast({ message: t('account:default_product_updated') });
                },
            }
        );

        setProduct(selectedProduct);
        navigateToProduct(navigation, selectedProduct, 'lobby');
    };

    return (
        <SheetScreen edges={['top', 'bottom']}>
            <ScreenNavBar sheet closeIconMode={'close'} title={t('common:default_product')} />
            <ScrollView>
                <MaxWidthWrapper paddingHorizontal={'s16'} paddingBottom={'s20'}>
                    <SizedBox value={8} />
                    <Text variant="bodySmall" color="neutral300">
                        {t('account:default_product_subtitle')}
                    </Text>
                    <SizedBox value={32} />
                    <AccountLink>
                        {allVerticals.map((vertical, index) => {
                            const product = mapDefaultVerticalToProduct(vertical);

                            if (product === Product.None) {
                                return null;
                            }

                            const isAvailable = isProductAvailable(product);
                            const isSelected = selectedVertical === vertical;
                            const isLastItem = index === allVerticals.length - 1;

                            return (
                                <React.Fragment key={vertical}>
                                    <TouchableOpacity
                                        activeOpacity={0.7}
                                        disabled={!isAvailable}
                                        onPress={() => setPendingVertical(vertical)}
                                    >
                                        <Row alignItems="center" justifyContent="space-between" paddingVertical="s16">
                                            <Text
                                                variant="titleMedium"
                                                color={isAvailable ? ds.colorToken.text.primary : 'neutral400'}
                                            >
                                                {t(product)}
                                            </Text>
                                            <RadioItem
                                                selected={isSelected}
                                                color={
                                                    isAvailable
                                                        ? ds.colorValue.icon.primary
                                                        : designSystem.colors.neutral400
                                                }
                                            />
                                        </Row>
                                    </TouchableOpacity>
                                    {!isLastItem ? <LineSeparator /> : null}
                                </React.Fragment>
                            );
                        })}
                    </AccountLink>
                    {hasDisabled ? (
                        <>
                            <SizedBox value={20} />
                            <Row
                                backgroundColor={ds.colorToken.background.neutral.subtler}
                                borderRadius="r12"
                                padding="s16"
                                gap="s12"
                                alignItems="center"
                            >
                                <InfoOutlinedIcon color={designSystem.colors.neutral300} />
                                <Box flex={1}>
                                    <Text variant="bodySmall">{t('account:default_product_disabled_notice')}</Text>
                                </Box>
                            </Row>
                        </>
                    ) : null}
                </MaxWidthWrapper>
            </ScrollView>
            <MaxWidthWrapper>
                <Box px={'s16'} pt={'s12'} pb={'s16'}>
                    <Button
                        label={t('common:update')}
                        hierarchy={'primary'}
                        onPress={handleUpdate}
                        disabled={!canUpdate}
                    />
                </Box>
            </MaxWidthWrapper>
        </SheetScreen>
    );
};
